/**
 * renders an image to the calling component. It typically is used for rendering proudct images. It has an intersection observer which is used for lazyloading.
 *
 * @module views/Multimedia/Image
 */
import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntersectionObserver } from '../../../utils/useIntersectionObserver/useIntersectionObserver';
import { isServer } from '../../../utils/device_detection/device_detection';
import { useDeviceInflection } from '../../__core/InflectionProvider/InflectionProvider';

import './Image.scss';
/**
  * Represents a Image component
  *
  * @method
  * @param { Object } props - React properties passed from composition
  * @returns Image
  */
export const Image = function( props ){
  // TODO the darkmode attribut will be a global setting at some point. for now it is local to the image for the background transfomration tempalte
  const isDarkMode = props.darkMode; // TEMPORARY: added as a prop for testing TODO is complete

  const imgRef = useRef( null );
  const { breakpoint, inflection } = useDeviceInflection();
  const [isLoadingImage, setIsLoadingImage] = useState( true )
  const [imgSrc, setImgSrc] = useState( '' );
  const [imageSet, setImageSet] = useState( breakpoint.BREAKPOINTS.reduce( ( acc, curr ) => ( acc[curr] = '', acc ), {} ) ); // eslint-disable-line

  const { hasIntersected } = useIntersectionObserver( imgRef, {
    root: props.root,
    rootMargin: props.rootMargin,
    threshold: props.threshold
  } );

  const showImage = isServer() ? false : props.src && hasIntersected;
  const prevActiveBreakpoint = useRef();

  let {
    width: metaDataWidth,
    height: metaDataHeight,
    imageTemplate
  } = props.metaData

  const placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

  let aspectRatio;

  const hasValidAspectRation =  /(\d+):(\d+)$/.test( props.aspectRatio );
  if( hasValidAspectRation ){
    // if an aspect ratio is provided then use that as the height/width for the metaData
    let [widthRatio, heightRatio] = aspectRatio = props.aspectRatio.split( ':' ).map( i => ( parseInt( i, 10 ) ) )
    metaDataHeight = metaDataWidth * heightRatio;
  }

  let shouldSetImageTemplate = !!imageTemplate && imageTemplate !== 'None'

  useEffect( () => {
    if( showImage ){
      // if the imgTag doesn't have a src tag then we will calculate the lowres image as a fraction of the max size
      let activeBreakpointIndex = breakpoint.BREAKPOINTS.findIndex( val => val === breakpoint.CURRENT_BREAKPOINT );
      let previousBreakpointIndex = breakpoint.BREAKPOINTS.findIndex( val => val === prevActiveBreakpoint.current );
      const imageSrcHasChanged = imageSet[ breakpoint.CURRENT_BREAKPOINT ].length > 5 && !imageSet[ breakpoint.CURRENT_BREAKPOINT ].includes( props.src );
      let imageParams;
      if( imageSrcHasChanged || ( activeBreakpointIndex > previousBreakpointIndex && !imageSrcHasChanged ) ){
        let imageWidth =  Math.round( ( props.width ? props.width : imgRef.current.offsetWidth ) * global.devicePixelRatio );
        // if the width of the image is greater than the largest in the dam default ot the larges size in the dam
        imageWidth = ( metaDataWidth && imageWidth > metaDataWidth ) ? metaDataWidth : imageWidth;
        imageParams = `w=${ imageWidth }`;
        if( shouldSetImageTemplate ){
          imageParams += `&$${ imageTemplate }${ isDarkMode ? 'Dark' : 'Light'}$`
        }

        if( hasValidAspectRation ){
          let [widthRatio, heightRatio] = aspectRatio

          // calculate the height
          imageParams += `&h=${ Math.round( ( heightRatio / widthRatio ) * imageWidth ) }`
          // set the metaData Height based on the aspect ratio value
          metaDataHeight = metaDataWidth * heightRatio;
        }

        const newImgSetSrc = `${ props.src }?${ imageParams }`;

        if( newImgSetSrc !== imgSrc ){
          setIsLoadingImage( true );

          setImageSet( {
            ...imageSet,
            [breakpoint.CURRENT_BREAKPOINT]: newImgSetSrc
          } )
          setImgSrc( newImgSetSrc );

          if( !props.isBackground ){
            let imgTag = imgRef.current.querySelector( 'img' );
            imgTag.onload = () => {
              setIsLoadingImage( false );
            }
          }
        }
        prevActiveBreakpoint.current = breakpoint.CURRENT_BREAKPOINT;
      }
    }

  }, [showImage, breakpoint.CURRENT_BREAKPOINT, inflection.MOBILE, imgSrc, props.src, props.aspectRatio, props.width, imgRef.current] );

  const imageStyles = {
    ...( props.width && { width: `${ props.width }px` } ),
    ...( props.height && { height: `${ props.height }px` } )
  }

  return (
    <div className='Image'
      ref={ imgRef }
      { ...( ( props.width || props.height ) && { style: imageStyles } ) }
      { ...( props.ariaLabel && { [`aria-label`]: props.ariaLabel } ) }
      { ...( props.isBackground && { style: { backgroundImage: `url( ${ !showImage ? `${props.src}?w=500` : imgSrc }${ shouldSetImageTemplate ? '&fmt=auto' : '' } )` } } ) }
    >
      {
        !props.isBackground &&
        <picture>
          { showImage && imgSrc &&
            <source
              srcSet={ `${ imgSrc }&fmt=webp` }
              type='image/webp'
            />
          }
          <img
            className={
              classNames( {
                'Image--loading': isLoadingImage
              } )
            }
            alt={ props.alt }
            { ...( props.ariaHidden && { [`aria-hidden`]: 'true' } ) }
            { ...( !showImage && { src: placeholder } ) }
            { ...( showImage && imgSrc && { src: `${ imgSrc }${ shouldSetImageTemplate ? '&fmt=auto' : '' }` } ) }
            { ...( props.id && { id: props.id } ) }
            { ...( props.metaData?.width && { width: metaDataWidth } ) }
            { ...( props.metaData?.height && { height: metaDataHeight } ) }
            { ...( props.ariaCurrent && { [`aria-current`]: props.ariaCurrent } ) }
          />
        </picture>
      }
    </div>
  )
}

/**
  * property type definitions
  * @type object
  * @property {string} src - sets the image src for image display
  * @property {string} alt - sets the alt text for image. Required for all images. If decorative image pass ''
  * @property {boolean} ariaHidden - sets the area hidden
  * @property {number} width - sets the image width
  * @property {number} aspectRatio - sets the images aspect ratio so that a specific size can be reuqested
  * @property {string} ariaLabel - Sets the ariaLabel for the image
  */
export const propTypes =  {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  ariaHidden: PropTypes.bool,
  width: PropTypes.number,
  aspectRatio: PropTypes.string,
  metaData: PropTypes.object,
  ariaLabel: PropTypes.string,
  isBackground: PropTypes.bool
};

/**
  * property type definitions
  * @type object
  * @property {string} [rootMargin='50px 50px'] - Margin around the root. If the image gets within 50px in the Y axis, start the download.
  * @property {object} [root = null] - The element that is used as the viewport for checking visiblity of the target.
  * @property {number} [threshold = 0.01] - Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed.
  */
export const defaultProps = {

  // If the image gets within 50px in the Y axis, start the download.
  rootMargin:'50px 50px',
  root : null,
  threshold: 0.01,
  alt:'',
  metaData: {
    width: undefined,
    height: undefined
  },
  isBackground: false
}

Image.propTypes = propTypes;
Image.defaultProps = defaultProps;

export default Image;

